import React, { useState, useEffect } from 'react';
import './DashboardSchoolStudents.css';
import '../../pages/PasswordVerification/PasswordVerification.css';
import { supabase } from '../../lib/supabase';
import { logUserActivity } from '../../lib/activityLogger';
import { UserIdGenerator } from '../../lib/userIdGenerator';
import NotificationModal from '../NotificationModal/NotificationModal';
import * as XLSX from 'xlsx';

// Helper function to convert database errors to user-friendly messages
const getFriendlyErrorMessage = (error, operation = 'operation') => {
  if (!error) return 'An unexpected error occurred.';
  
  const errorMessage = error.message || String(error);
  const errorCode = error.code || '';
  
  // Duplicate email error
  if (errorMessage.includes('users_school_id_email_key') || 
      (errorMessage.includes('duplicate key') && errorMessage.includes('email'))) {
    return 'This email address is already registered for a student in this school. Please use a different email address.';
  }
  
  // Duplicate user_id error
  if (errorMessage.includes('users_user_id_key') || 
      (errorMessage.includes('duplicate key') && errorMessage.includes('user_id'))) {
    return 'A user with this ID already exists. Please try again.';
  }
  
  // Foreign key constraint errors
  if (errorMessage.includes('foreign key constraint') || errorMessage.includes('violates foreign key')) {
    if (errorMessage.includes('school_id')) {
      return 'Invalid school selected. Please refresh the page and try again.';
    }
    if (errorMessage.includes('grade_id')) {
      return 'Invalid grade selected. Please select a valid grade.';
    }
    if (errorMessage.includes('grade_section_id')) {
      return 'Invalid grade section selected. Please select a valid section.';
    }
    return 'Invalid data provided. Please check your selections and try again.';
  }
  
  // Not null constraint errors
  if (errorMessage.includes('null value') && errorMessage.includes('violates not-null constraint')) {
    return 'Please fill in all required fields.';
  }
  
  // Network/connection errors
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorCode === 'PGRST116') {
    return 'Network error. Please check your internet connection and try again.';
  }
  
  // Return original message if no specific pattern matches
  return errorMessage;
};

const NoDataState = ({ type, icon, message, actionText, onAction }) => (
  <div className="school-no-data-state">
    <div className="school-no-data-icon">{icon}</div>
    <h3>No {type} Found</h3>
    <p>{message}</p>
    {onAction && (
      <button className="school-add-first-btn" onClick={onAction}>{actionText}</button>
    )}
  </div>
);

const DashboardSchoolStudents = ({ students, grades, gradeSections, dataLoading, onStudentAdded, readOnly = false }) => {
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('alphabet');
  const [filterValue, setFilterValue] = useState('');
  const [filterGradeId, setFilterGradeId] = useState('');
  const [filterGradeSectionId, setFilterGradeSectionId] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [studentFormData, setStudentFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    grade_id: '',
    grade_section_id: '',
    language: '',
    is_active: true,
    password: ''
  });
  const [newPassword, setNewPassword] = useState(''); // Separate state for password editing
  const [addMode, setAddMode] = useState('single'); // 'single' or 'excel'
  const [excelFile, setExcelFile] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [isExcelUploading, setIsExcelUploading] = useState(false);
  const [excelGradeId, setExcelGradeId] = useState('');
  const [excelGradeSectionId, setExcelGradeSectionId] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showDeleteByExcelModal, setShowDeleteByExcelModal] = useState(false);
  const [deleteExcelFile, setDeleteExcelFile] = useState(null);
  const [isDeleteExcelUploading, setIsDeleteExcelUploading] = useState(false);
  const [deleteExcelDragging, setDeleteExcelDragging] = useState(false);
  const [showDeleteExcelResults, setShowDeleteExcelResults] = useState(false);
  const [deleteExcelResults, setDeleteExcelResults] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkPromoteConfirm, setShowBulkPromoteConfirm] = useState(false);
  const [promotionPreview, setPromotionPreview] = useState({ fromGrade: '', toGrade: '', count: 0, isGraduation: false });

  const STUDENTS_PER_PAGE = 100;
  const [showMaxLimitError, setShowMaxLimitError] = useState(false);
  const [maxLimitError, setMaxLimitError] = useState({ type: '', current: 0, max: 0 });
  const [activeStudentsCount, setActiveStudentsCount] = useState(0);
  const [maxStudents, setMaxStudents] = useState(null);

  // If the dashboard enters read-only mode, force-disable any destructive UX paths.
  useEffect(() => {
    if (!readOnly) return;
    setSelectionMode(false);
    setSelectedStudents(new Set());
    setShowAddStudentModal(false);
    setShowDetailsModal(false);
    setIsEditing(false);
  }, [readOnly]);

  // Fetch and update active students count and max limit
  useEffect(() => {
    const fetchCounts = async () => {
      const [currentCount, maxLimit] = await Promise.all([
        getActiveStudentsCount(),
        getSchoolMaxStudents()
      ]);
      setActiveStudentsCount(currentCount);
      setMaxStudents(maxLimit);
    };
    fetchCounts();
  }, [students]); // Re-fetch when students list changes

  // Set initial form data when modal opens
  useEffect(() => {
    if (showAddStudentModal && !initialFormData) {
      setInitialFormData(JSON.parse(JSON.stringify(studentFormData)));
    } else if (!showAddStudentModal) {
      setInitialFormData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddStudentModal]);

  // Helper function to get school's max_students limit
  const getSchoolMaxStudents = async () => {
    try {
      const schoolId = localStorage.getItem('school_id');
      if (!schoolId) return null;

      const { data, error } = await supabase
        .from('schools')
        .select('max_students')
        .eq('id', schoolId)
        .single();

      if (error) {return null;
      }

      return data?.max_students ?? null;
    } catch (err) {return null;
    }
  };

  // Helper function to count active students for the school (excluding graduated)
  const getActiveStudentsCount = async () => {
    try {
      const schoolId = localStorage.getItem('school_id');
      if (!schoolId) return 0;

      const { count, error } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', schoolId)
        .eq('role', 'student')
        .eq('is_active', true);

      if (error) {return 0;
      }

      return count || 0;
    } catch (err) {return 0;
    }
  };

  // Helper function to check if adding a student would exceed the limit
  const checkStudentLimit = async (isActive = true) => {
    if (!isActive) return { allowed: true }; // Inactive students don't count

    const maxStudents = await getSchoolMaxStudents();
    if (maxStudents === null || maxStudents === undefined) {
      return { allowed: true }; // No limit set
    }

    const currentCount = await getActiveStudentsCount();
    if (currentCount >= maxStudents) {
      return {
        allowed: false,
        current: currentCount,
        max: maxStudents
      };
    }

    return { allowed: true, current: currentCount, max: maxStudents };
  };

  // Promotion helpers (string-based grades: KG1/KG2/KG3/Grade 1..Grade 12)
  const parseGradeForPromotion = (gradeValue) => {
    const raw = String(gradeValue || '').trim();
    if (!raw) return { type: 'unknown', value: '' };
    const lower = raw.toLowerCase();
    if (lower.startsWith('kg')) {
      const num = raw.replace(/kg/i, '').trim().replace(/\s+/g, '');
      const n = Number(num);
      if (!Number.isFinite(n) || n <= 0) return { type: 'unknown', value: raw };
      return { type: 'kg', value: n };
    }
    const m = raw.match(/^grade\s*(\d{1,2})$/i);
    if (m) {
      const n = Number(m[1]);
      if (!Number.isFinite(n) || n <= 0) return { type: 'unknown', value: raw };
      return { type: 'grade', value: n };
    }
    if (/^\d{1,2}$/.test(raw)) {
      const n = Number(raw);
      return { type: 'grade', value: n };
    }
    return { type: 'unknown', value: raw };
  };

  const getNextGradeLabel = (gradeValue) => {
    const parsed = parseGradeForPromotion(gradeValue);
    if (parsed.type === 'kg') {
      if (parsed.value === 1) return 'KG2';
      if (parsed.value === 2) return 'KG3';
      if (parsed.value === 3) return 'Grade 1';
      return `KG${parsed.value + 1}`;
    }
    if (parsed.type === 'grade') {
      if (parsed.value >= 12) return null;
      return `Grade ${parsed.value + 1}`;
    }
    return null;
  };

  // Generate random 6-digit password
  const generateRandomPassword = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Filter and pagination logic
  useEffect(() => {
    let filtered = [...students];

    switch (filterType) {
      case 'alphabet':
        filtered = filtered.sort((a, b) => {
          const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
          const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'grade':
        if (filterGradeId) {
          filtered = filtered.filter(student => {
            // Filter by grade_id
            if (student.grade_id === filterGradeId) {
              // If grade section filter is also set, filter by that too
              if (filterGradeSectionId) {
                return student.grade_section_id === filterGradeSectionId;
              }
              return true;
            }
            return false;
          });
        }
        break;
      case 'status':
        if (filterValue) {
          if (filterValue === 'graduated') {
            filtered = filtered.filter(student => student.is_graduated);
          } else if (filterValue === 'active') {
            filtered = filtered.filter(student => student.is_active && !student.is_graduated);
          } else if (filterValue === 'inactive') {
            filtered = filtered.filter(student => !student.is_active && !student.is_graduated);
          }
        }
        break;
      default:
        break;
    }

    setFilteredStudents(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [students, filterType, filterValue, filterGradeId, filterGradeSectionId]);

  const totalPages = Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * STUDENTS_PER_PAGE;
  const endIndex = startIndex + STUDENTS_PER_PAGE;
  const currentStudents = filteredStudents.slice(startIndex, endIndex);

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  // Check if form has been modified
  const hasFormChanges = () => {
    if (!initialFormData) return false;
    return JSON.stringify(studentFormData) !== JSON.stringify(initialFormData);
  };

  // Handle overlay click with unsaved changes check
  const handleOverlayClick = (closeAction) => {
    if (hasFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialFormData(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setStudentFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'radio' ? value === 'true' : value)
    }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    // Only allow numbers for password field
    const numericValue = value.replace(/\D/g, '').slice(0, 6); // Limit to 6 digits
    setNewPassword(numericValue);
  };

  const handlePasswordSquareClick = () => {
    const hiddenInput = document.getElementById('dashboard-school-students-password-input');
    if (hiddenInput) {
      hiddenInput.focus();
    }
  };

  // Build list of grade/section options for display (same UX as DashboardSchoolTeacher)
  // Returns array of { key, label, gradeId, sectionId, isSection }
  const buildGradeSectionOptions = () => {
    const options = [];

    (grades || [])
      .filter(g => g.is_active)
      .sort((a, b) => a.order_index - b.order_index)
      .forEach(grade => {
        const sections = (gradeSections || [])
          .filter(s => s.grade_id === grade.id && s.is_active)
          .sort((a, b) => a.order_index - b.order_index);

        if (sections.length > 0) {
          sections.forEach(section => {
            options.push({
              key: `${grade.id}:${section.id}`,
              label: `${grade.name} ${section.name}`,
              gradeId: grade.id,
              sectionId: section.id,
              isSection: true
            });
          });
        } else {
          options.push({
            key: grade.id,
            label: grade.name,
            gradeId: grade.id,
            sectionId: null,
            isSection: false
          });
        }
      });

    return options;
  };

  const getSelectedGradeChoiceValue = () => {
    if (!studentFormData.grade_id) return '';
    if (studentFormData.grade_section_id) return `${studentFormData.grade_id}:${studentFormData.grade_section_id}`;
    return studentFormData.grade_id;
  };

  const handleGradeChoiceChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setStudentFormData(prev => ({ ...prev, grade_id: '', grade_section_id: '' }));
      return;
    }

    if (value.includes(':')) {
      const [gradeId, sectionId] = value.split(':');
      setStudentFormData(prev => ({ ...prev, grade_id: gradeId, grade_section_id: sectionId }));
      return;
    }

    setStudentFormData(prev => ({ ...prev, grade_id: value, grade_section_id: '' }));
  };

  const openStudentDetails = (student) => {
    setSelectedStudent(student);
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const startEditSelectedStudent = () => {
    if (!selectedStudent) return;
    setStudentFormData({
      first_name: selectedStudent.first_name || '',
      last_name: selectedStudent.last_name || '',
      email: selectedStudent.email || '',
      phone: selectedStudent.phone || '',
      grade_id: selectedStudent.grade_id || '',
      grade_section_id: selectedStudent.grade_section_id || '',
      language: selectedStudent.language || '',
      is_active: selectedStudent.is_active !== false
    });
    setNewPassword(selectedStudent.password || ''); // Load existing password when editing

    setIsEditing(true);
  };

  // Generate unique user_id based on school_id and incremental number
  const generateUserId = async () => {
    try {
      const schoolId = localStorage.getItem('school_id');

      // Get the school's school_id from the schools table
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('school_id')
        .eq('id', schoolId)
        .single();

      if (schoolError) {return null;
      }

      const schoolCode = schoolData.school_id;

      // Use the enhanced UserIdGenerator
      return await UserIdGenerator.generateUserId(schoolCode);
    } catch (err) {return null;
    }
  };

  const handleSubmitStudent = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setIsSubmitting(true);

    try {
      // Check max students limit if adding as active
      if (studentFormData.is_active) {
        const limitCheck = await checkStudentLimit(true);
        if (!limitCheck.allowed) {
          setMaxLimitError({
            type: 'students',
            current: limitCheck.current,
            max: limitCheck.max
          });
          setShowMaxLimitError(true);
          setIsSubmitting(false);
          return;
        }
      }

      // Generate unique user_id
      const userId = await generateUserId();
      if (!userId) {
        alert('Error generating user ID. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Generate random 6-digit password
      const randomPassword = generateRandomPassword();
      const schoolId = localStorage.getItem('school_id');

      // Get grade name for the grade_id
      const selectedGrade = grades.find(g => g.id === studentFormData.grade_id);
      const gradeName = selectedGrade ? selectedGrade.name : null;

      // Ensure grade_id is null if empty string, otherwise use the value
      const gradeId = studentFormData.grade_id && studentFormData.grade_id.trim() !== ''
        ? studentFormData.grade_id
        : null;
      const gradeSectionId = studentFormData.grade_section_id && studentFormData.grade_section_id.trim() !== ''
        ? studentFormData.grade_section_id
        : null;

      const { error } = await supabase
        .from('users')
        .insert([{
          user_id: userId,
          first_name: studentFormData.first_name,
          last_name: studentFormData.last_name,
          school_id: schoolId,
          email: studentFormData.email,
          phone: studentFormData.phone || null,
          role: 'student',
          grade: gradeName,
          grade_id: gradeId,
          grade_section_id: gradeSectionId,
          language: studentFormData.language || null,
          is_active: studentFormData.is_active,
          password: randomPassword,
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) {const friendlyMessage = getFriendlyErrorMessage(error, 'adding student');
        showNotification('error', 'Error Adding Student', friendlyMessage);
      } else {
        // Log the activity with school_id
        const studentName = `${studentFormData.first_name} ${studentFormData.last_name}`;
        await logUserActivity.created(studentName, 'School Students', schoolId);

        showNotification('success', 'Student Added Successfully', `${studentName} has been added with ID: ${userId} and Password: ${randomPassword}`);
        setStudentFormData({
          first_name: '', last_name: '', email: '', phone: '', grade_id: '', grade_section_id: '', language: '', is_active: true, password: ''
        });
        setInitialFormData(null);
        setShowAddStudentModal(false);
        onStudentAdded && onStudentAdded();
      }
    } catch (err) {const friendlyMessage = getFriendlyErrorMessage(err, 'adding student');
      showNotification('error', 'Error Adding Student', friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    if (readOnly) return;
    setIsSubmitting(true);

    try {
      // Check max students limit if changing status to active
      const wasInactive = !selectedStudent.is_active;
      const willBeActive = studentFormData.is_active;
      if (wasInactive && willBeActive) {
        const limitCheck = await checkStudentLimit(true);
        if (!limitCheck.allowed) {
          setMaxLimitError({
            type: 'students',
            current: limitCheck.current,
            max: limitCheck.max
          });
          setShowMaxLimitError(true);
          setIsSubmitting(false);
          return;
        }
      }

      // Get grade name for the grade_id
      const selectedGrade = grades.find(g => g.id === studentFormData.grade_id);
      const gradeName = selectedGrade ? selectedGrade.name : null;

      // Ensure grade_id is null if empty string, otherwise use the value
      const gradeId = studentFormData.grade_id && studentFormData.grade_id.trim() !== ''
        ? studentFormData.grade_id
        : null;
      const gradeSectionId = studentFormData.grade_section_id && studentFormData.grade_section_id.trim() !== ''
        ? studentFormData.grade_section_id
        : null;

      // Build update object
      const updateData = {
        first_name: studentFormData.first_name,
        last_name: studentFormData.last_name,
        email: studentFormData.email,
        phone: studentFormData.phone || null,
        grade: gradeName,
        grade_id: gradeId,
        grade_section_id: gradeSectionId,
        language: studentFormData.language || null,
        is_active: studentFormData.is_active,
        updated_at: new Date().toISOString()
      };

      // Graduation rule:
      // - If the new grade is NOT the last grade in the school, the student must NOT be graduated.
      //   So we force is_graduated = null, and if they were graduated before, we re-activate them.
      try {
        const schoolId = localStorage.getItem('school_id') || selectedStudent?.school_id;
        if (schoolId && gradeId) {
          const { data: allGrades, error: gradesError } = await supabase
            .from('grades')
            .select('id, order_index')
            .eq('school_id', schoolId)
            .eq('is_active', true)
            .order('order_index', { ascending: false })
            .limit(1);

          const lastGradeId = !gradesError && allGrades && allGrades.length ? allGrades[0].id : null;
          if (lastGradeId && String(gradeId) !== String(lastGradeId)) {
            updateData.is_graduated = null;
            if (selectedStudent?.is_graduated) updateData.is_active = true;
          }
        } else {
          // No grade selected means they are not in "last grade" => not graduated.
          updateData.is_graduated = null;
          if (selectedStudent?.is_graduated) updateData.is_active = true;
        }
      } catch (err) {
        // If anything goes wrong, still prefer clearing graduation when editing grade away from graduation.
        updateData.is_graduated = null;
        if (selectedStudent?.is_graduated) updateData.is_active = true;}

      // Only update password if a new one is provided
      if (newPassword && newPassword.trim() !== '') {
        updateData.password = newPassword.trim();
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', selectedStudent.id);

      if (error) {const friendlyMessage = getFriendlyErrorMessage(error, 'updating student');
        showNotification('error', 'Error Updating Student', friendlyMessage);
      } else {
        // Log the activity with school_id
        const schoolId = localStorage.getItem('school_id');
        const studentName = `${studentFormData.first_name} ${studentFormData.last_name}`;
        await logUserActivity.updated(studentName, schoolId);

        const passwordMsg = newPassword && newPassword.trim() !== '' ? ' Password has been updated.' : '';
        showNotification('success', 'Student Updated Successfully', `${studentName} has been updated successfully.${passwordMsg}`);
        setShowDetailsModal(false);
        setIsEditing(false);
        setNewPassword(''); // Clear password field after successful update
        onStudentAdded && onStudentAdded();
      }
    } catch (err) {const friendlyMessage = getFriendlyErrorMessage(err, 'updating student');
      showNotification('error', 'Error Updating Student', friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStudent = () => {
    if (readOnly) return;
    if (!selectedStudent) return;
    setItemToDelete({ type: 'student', data: selectedStudent });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (readOnly) return;
    if (!itemToDelete) return;

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', itemToDelete.data.id);

      if (error) {showNotification('error', 'Error Deleting Student', error.message);
      } else {
        // Log the activity with school_id
        const schoolId = localStorage.getItem('school_id');
        const studentName = `${itemToDelete.data.first_name} ${itemToDelete.data.last_name}`;
        await logUserActivity.deleted(studentName, schoolId);

        showNotification('success', 'Student Deleted Successfully', `${studentName} has been deleted successfully`);
        setShowDetailsModal(false);
        setSelectedStudent(null);
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        onStudentAdded && onStudentAdded();
      }
    } catch (err) {showNotification('error', 'Error Deleting Student', err.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // Bulk selection functions
  const toggleSelectionMode = () => {
    if (readOnly) return;
    setSelectionMode(!selectionMode);
    setSelectedStudents(new Set());
  };

  const toggleSelectAll = () => {
    if (selectedStudents.size === currentStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(currentStudents.map(s => s.id)));
    }
  };

  const toggleSelectStudent = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleBulkDelete = () => {
    if (readOnly) return;
    if (selectedStudents.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkPromote = () => {
    if (readOnly) return;
    if (selectedStudents.size === 0) return;

    const selectedList = students.filter(s => selectedStudents.has(s.id));

    // Get unique grade_ids (not grade strings, since students in subsections have same grade_id)
    const uniqueGradeIds = Array.from(new Set(selectedList.map(s => s.grade_id).filter(Boolean)));

    if (uniqueGradeIds.length === 0) {
      showNotification('warning', 'Promotion Not Possible', 'Selected students do not have a grade assigned.');
      return;
    }

    if (uniqueGradeIds.length > 1) {
      showNotification('warning', 'Select One Grade', 'Please select students from the same grade to promote together.');
      return;
    }

    // Get the current grade object
    const currentGradeId = uniqueGradeIds[0];
    const currentGrade = grades.find(g => g.id === currentGradeId && g.is_active);

    if (!currentGrade) {
      showNotification('warning', 'Promotion Not Possible', 'Could not find the current grade.');
      return;
    }

    // Find the next grade by order_index (first grade with order_index > current)
    const nextGrade = grades
      .filter(g => g.is_active)
      .sort((a, b) => a.order_index - b.order_index)
      .find(g => g.order_index > currentGrade.order_index);

    // If no next grade, this is graduation - deactivate accounts
    if (!nextGrade) {
      setPromotionPreview({
        fromGrade: currentGrade.name,
        toGrade: null,
        count: selectedStudents.size,
        fromGradeId: currentGrade.id,
        toGradeId: null,
        isGraduation: true
      });
      setShowBulkPromoteConfirm(true);
      return;
    }

    setPromotionPreview({
      fromGrade: currentGrade.name,
      toGrade: nextGrade.name,
      count: selectedStudents.size,
      fromGradeId: currentGrade.id,
      toGradeId: nextGrade.id,
      isGraduation: false
    });
    setShowBulkPromoteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (readOnly) return;
    try {
      const studentIds = Array.from(selectedStudents);
      const { error } = await supabase
        .from('users')
        .delete()
        .in('id', studentIds);

      if (error) {showNotification('error', 'Bulk Delete Failed', error.message);
      } else {
        const schoolId = localStorage.getItem('school_id');
        await logUserActivity.deleted(`${studentIds.length} students (bulk delete)`, schoolId);

        showNotification('success', 'Students Deleted Successfully', `${studentIds.length} student(s) have been deleted`);
        setSelectedStudents(new Set());
        setSelectionMode(false);
        setShowBulkDeleteConfirm(false);
        onStudentAdded && onStudentAdded();
      }
    } catch (err) {showNotification('error', 'Bulk Delete Failed', err.message);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
  };

  const confirmBulkPromote = async () => {
    if (readOnly) return;
    try {
      const studentIds = Array.from(selectedStudents);

      // Handle graduation vs normal promotion
      if (promotionPreview.isGraduation) {
        // Graduation: set is_graduated to current date and is_active to false
        const graduationDate = new Date().toISOString();
        const { error } = await supabase
          .from('users')
          .update({
            is_graduated: graduationDate,
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .in('id', studentIds);

        if (error) {showNotification('error', 'Graduation Failed', error.message);
          return;
        }

        const schoolId = localStorage.getItem('school_id');
        await logUserActivity.updated(
          `${studentIds.length} students graduated from ${promotionPreview.fromGrade}`,
          schoolId
        );

        showNotification(
          'success',
          'Students Graduated',
          `${studentIds.length} student(s) have graduated from ${promotionPreview.fromGrade}.`
        );
      } else {
        // Normal promotion: update to next grade
        const targetGrade = grades.find(g => g.id === promotionPreview.toGradeId && g.is_active);

        if (!targetGrade) {
          showNotification('error', 'Promotion Failed', `Could not find target grade in the system.`);
          return;
        }

        const { error } = await supabase
          .from('users')
          .update({
            grade: targetGrade.name,
            grade_id: targetGrade.id,
            grade_section_id: null, // Clear section when promoting to new grade
            updated_at: new Date().toISOString()
          })
          .in('id', studentIds);

        if (error) {showNotification('error', 'Promotion Failed', error.message);
          return;
        }

        const schoolId = localStorage.getItem('school_id');
        await logUserActivity.updated(
          `${studentIds.length} students promoted (${promotionPreview.fromGrade} → ${promotionPreview.toGrade})`,
          schoolId
        );

        showNotification(
          'success',
          'Students Promoted',
          `${studentIds.length} student(s) promoted from ${promotionPreview.fromGrade} to ${promotionPreview.toGrade}.`
        );
      }

      setSelectedStudents(new Set());
      setSelectionMode(false);
      setShowBulkPromoteConfirm(false);
      onStudentAdded && onStudentAdded();
    } catch (err) {showNotification('error', 'Promotion Failed', err.message);
    }
  };

  const cancelBulkPromote = () => {
    setShowBulkPromoteConfirm(false);
  };

  const closeModal = () => {
    setShowAddStudentModal(false);
    setAddMode('single');
    setExcelFile(null);
    setExcelGradeId('');
    setExcelGradeSectionId('');
    setStudentFormData({
      first_name: '', last_name: '', email: '', phone: '', grade_id: '', grade_section_id: '', language: '', is_active: true, password: ''
    });
    setShowUploadResults(false);
    setUploadResults(null);
    setInitialFormData(null);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const downloadDeleteExcelTemplate = () => {
    const templateData = [
      { first_name: 'John', last_name: 'Doe', email: 'john.doe@example.com' },
      { first_name: 'Jane', last_name: 'Smith', email: 'jane.smith@example.com' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');

    XLSX.writeFile(wb, 'delete_students_template.xls');
  };

  const downloadFailedDeleteReport = () => {
    if (!deleteExcelResults || !deleteExcelResults.failed || deleteExcelResults.failed.length === 0) return;

    // Helper function to create safe sheet names
    const createSafeSheetName = (name) => {
      let safeName = name
        .replace(/:/g, ' -')
        .replace(/[\\/?*[\]]/g, '')
        .substring(0, 31);
      return safeName || 'Unknown';
    };

    // Group failed students by reason
    const groupedByReason = deleteExcelResults.failed.reduce((acc, item) => {
      const reason = item.reason || 'Unknown error';
      if (!acc[reason]) {
        acc[reason] = [];
      }
      acc[reason].push(item);
      return acc;
    }, {});

    const wb = XLSX.utils.book_new();

    // Create summary sheet
    const summaryData = [
      { 'Summary': 'Failed to Deactivate Students Report', '': '' },
      { 'Summary': '', '': '' },
      { 'Summary': 'Total Failed', 'Count': deleteExcelResults.failed.length },
      { 'Summary': 'Total Processed', 'Count': deleteExcelResults.total },
      { 'Summary': 'Successfully Deactivated', 'Count': deleteExcelResults.success },
      { 'Summary': '', '': '' },
      { 'Summary': 'Failed by Reason', 'Count': '' },
    ];

    Object.entries(groupedByReason).forEach(([reason, students]) => {
      summaryData.push({
        'Summary': reason,
        'Count': students.length
      });
    });

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Create separate sheet for each reason
    Object.entries(groupedByReason).forEach(([reason, students]) => {
      const sheetData = students.map(item => ({
        'Row': item.row || '',
        'First Name': item.first_name || '',
        'Last Name': item.last_name || '',
        'Email': item.email || ''
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = createSafeSheetName(reason);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, 'failed_delete_students_report.xlsx');
  };

  const handleDeleteExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processDeleteExcelFile(file);
    }
  };

  const processDeleteExcelFile = (file) => {
    const name = (file?.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    if (file && isExcel) {
      setDeleteExcelFile(file);
    } else {
      showNotification('error', 'Invalid File', 'Please select a valid Excel file (.xlsx or .xls)');
    }
  };

  const handleDeleteExcelDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDeleteExcelDragging(true);
  };

  const handleDeleteExcelDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDeleteExcelDragging(false);
    }
  };

  const handleDeleteExcelDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteExcelDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processDeleteExcelFile(files[0]);
    }
  };

  const handleDeleteByExcel = async () => {
    if (!deleteExcelFile) {
      showNotification('error', 'Delete Failed', 'Please select an Excel file.');
      return;
    }

    setIsDeleteExcelUploading(true);

    try {
      const rows = await readExcelFirstSheet(deleteExcelFile);
      if (!rows || rows.length === 0) {
        showNotification('error', 'Delete Failed', 'Your Excel file is empty (no rows found).');
        return;
      }

      const schoolId = localStorage.getItem('school_id');

      const toDeactivate = [];
      const failed = [];
      const seenEmails = new Set();

      for (let i = 0; i < rows.length; i++) {
        const originalRow = rows[i];
        const normalizedRow = {};
        Object.entries(originalRow || {}).forEach(([k, v]) => {
          const nk = normalizeExcelHeader(k);
          if (nk) normalizedRow[nk] = typeof v === 'string' ? v.trim() : v;
        });

        const email = String(
          getExcelValue(normalizedRow, ['email', 'email_address', 'mail'])
        ).trim();

        // Skip blank rows
        if (!email) continue;

        // Validate email format
        if (!validateEmail(email)) {
          failed.push({
            row: i + 2,
            first_name: String(getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])).trim() || '',
            last_name: String(getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])).trim() || '',
            email: email,
            reason: 'Invalid email format'
          });
          continue;
        }

        // Check for duplicate email within the Excel file
        if (seenEmails.has(email.toLowerCase())) {
          failed.push({
            row: i + 2,
            first_name: String(getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])).trim() || '',
            last_name: String(getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])).trim() || '',
            email: email,
            reason: 'Email already exists in this Excel file'
          });
          continue;
        }
        seenEmails.add(email.toLowerCase());

        toDeactivate.push(email.toLowerCase());
      }

      if (toDeactivate.length === 0) {
        showNotification('error', 'Delete Failed', 'No valid emails found in the Excel file.');
        return;
      }

      // Find students by email in the database
      const CHECK_CHUNK = 200;
      const foundStudents = [];

      for (let i = 0; i < toDeactivate.length; i += CHECK_CHUNK) {
        const chunk = toDeactivate.slice(i, i + CHECK_CHUNK);
        const { data, error } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, is_active')
          .eq('school_id', schoolId)
          .eq('role', 'student')
          .in('email', chunk);

        if (error) {continue;
        }

        if (data) {
          foundStudents.push(...data);
        }
      }

      // Create a map of found students by email
      const foundStudentsMap = new Map();
      foundStudents.forEach(student => {
        foundStudentsMap.set(student.email.toLowerCase(), student);
      });

      // Separate found and not found
      const foundEmails = [];
      const notFoundEmails = [];

      toDeactivate.forEach(email => {
        const student = foundStudentsMap.get(email);
        if (student) {
          foundEmails.push({ email, student });
        } else {
          // Find the row data for this email
          for (let i = 0; i < rows.length; i++) {
            const originalRow = rows[i];
            const normalizedRow = {};
            Object.entries(originalRow || {}).forEach(([k, v]) => {
              const nk = normalizeExcelHeader(k);
              if (nk) normalizedRow[nk] = typeof v === 'string' ? v.trim() : v;
            });
            const rowEmail = String(getExcelValue(normalizedRow, ['email', 'email_address', 'mail'])).trim().toLowerCase();
            if (rowEmail === email) {
              notFoundEmails.push({
                row: i + 2,
                first_name: String(getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])).trim() || '',
                last_name: String(getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])).trim() || '',
                email: email
              });
              break;
            }
          }
        }
      });

      // Mark found students as inactive
      const CHUNK_SIZE = 50;
      let deactivatedCount = 0;
      const deactivationErrors = [];

      for (let i = 0; i < foundEmails.length; i += CHUNK_SIZE) {
        const chunk = foundEmails.slice(i, i + CHUNK_SIZE);
        const studentIds = chunk.map(item => item.student.id);

        const { error } = await supabase
          .from('users')
          .update({
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .in('id', studentIds);

        if (error) {chunk.forEach(item => {
            failed.push({
              row: null,
              first_name: item.student.first_name || '',
              last_name: item.student.last_name || '',
              email: item.email,
              reason: error.message || 'Failed to deactivate student'
            });
          });
          deactivationErrors.push(error.message);
        } else {
          deactivatedCount += chunk.length;
        }
      }

      // Add not found emails to failed list
      notFoundEmails.forEach(item => {
        failed.push({
          ...item,
          reason: 'Student not found in the system'
        });
      });

      // Log activity
      if (deactivatedCount > 0) {
        await logUserActivity.updated(`${deactivatedCount} students deactivated (Excel)`, schoolId);
      }

      // Set results
      const results = {
        total: rows.length,
        success: deactivatedCount,
        failed: failed,
        hasErrors: failed.length > 0 || deactivationErrors.length > 0
      };

      setDeleteExcelResults(results);
      setShowDeleteExcelResults(true);

      // Refresh student list if any were deactivated
      if (deactivatedCount > 0) {
        onStudentAdded && onStudentAdded();
      }
    } catch (err) {showNotification('error', 'Delete Failed', err.message || 'Unable to process the Excel file.');
    } finally {
      setIsDeleteExcelUploading(false);
    }
  };

  const downloadFailedUsersReport = () => {
    if (!uploadResults || !uploadResults.failed || uploadResults.failed.length === 0) return;

    // Helper function to create safe sheet names (Excel has restrictions)
    const createSafeSheetName = (name) => {
      // Excel sheet names can't be longer than 31 characters and can't contain certain characters
      let safeName = name
        .replace(/:/g, ' -') // Replace colons first
        .replace(/[\\/?*[\]]/g, '') // Remove invalid characters
        .substring(0, 31); // Limit to 31 characters
      return safeName || 'Unknown';
    };

    // Group failed students by reason
    const groupedByReason = uploadResults.failed.reduce((acc, item) => {
      const reason = item.reason || 'Unknown error';
      if (!acc[reason]) {
        acc[reason] = [];
      }
      acc[reason].push(item);
      return acc;
    }, {});

    const wb = XLSX.utils.book_new();

    // Create a summary sheet with overview
    const summaryData = [
      { 'Summary': 'Failed Students Upload Report', '': '' },
      { 'Summary': '', '': '' },
      { 'Summary': 'Total Failed', 'Count': uploadResults.failed.length },
      { 'Summary': 'Total Processed', 'Count': uploadResults.total },
      { 'Summary': 'Successfully Added', 'Count': uploadResults.success },
      { 'Summary': '', '': '' },
      { 'Summary': 'Failed by Reason', 'Count': '' },
    ];

    Object.entries(groupedByReason).forEach(([reason, students]) => {
      summaryData.push({
        'Summary': reason,
        'Count': students.length
      });
    });

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Create a separate sheet for each reason
    Object.entries(groupedByReason).forEach(([reason, students]) => {
      const sheetData = students.map(item => ({
        'Row': item.row || '',
        'First Name': item.first_name || '',
        'Last Name': item.last_name || '',
        'Email': item.email || ''
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = createSafeSheetName(reason);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, 'failed_students_report.xlsx');
  };

  // Excel handling functions
  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processExcelFile(file);
    }
  };

  const processExcelFile = (file) => {
    const name = (file?.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    if (file && isExcel) {
      setExcelFile(file);
    } else {
      showNotification('error', 'Invalid File', 'Please select a valid Excel file (.xlsx or .xls)');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show drag effect if file upload is allowed
    if (canUploadFile()) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragging(true);
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if we're actually leaving the drop zone (not just moving to a child element)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!canUploadFile()) {
      showNotification('warning', 'Cannot Upload', 'Please select grade' + (excelGradeId && !excelGradeSectionId ? ' and section' : '') + ' first.');
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processExcelFile(files[0]);
    }
  };

  const canUploadFile = () => {
    if (!excelGradeId) return false;

    // Check if grade has subsections
    const availableSections = gradeSections
      .filter(section => section.grade_id === excelGradeId && section.is_active);

    // If grade has subsections, subsection must be selected
    if (availableSections.length > 0) {
      return excelGradeSectionId !== '';
    }

    // If no subsections, grade selection is enough
    return true;
  };

  const downloadExcelTemplate = () => {
    const templateData = [
      { first_name: 'John', last_name: 'Doe', email: 'john.doe@example.com' },
      { first_name: 'Jane', last_name: 'Smith', email: 'jane.smith@example.com' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');

    // Save file with .xls extension
    // Note: XLSX.js will save as .xlsx format by default, but the .xls extension is kept for compatibility
    XLSX.writeFile(wb, 'students_template.xls');
  };

  const normalizeExcelHeader = (header) => {
    if (header === null || header === undefined) return '';
    return String(header)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  };

  const getExcelValue = (row, keys) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') {
        return row[k];
      }
    }
    return '';
  };

  const readExcelFirstSheet = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames?.[0];
          if (!firstSheetName) return resolve([]);
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          resolve(rows || []);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read the Excel file'));
      reader.readAsArrayBuffer(file);
    });

  const normalizeGrade = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const lower = raw.toLowerCase();
    if (lower.startsWith('kg')) {
      const num = raw.replace(/kg/i, '').trim().replace(/\s+/g, '');
      return num ? `KG${num}` : raw;
    }
    if (lower.startsWith('grade')) {
      const num = raw.replace(/grade/i, '').trim();
      return num ? `Grade ${num}` : raw;
    }
    if (/^\d+$/.test(raw)) return `Grade ${raw}`;
    return raw;
  };

  const parseIsActive = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return true;
    if (['active', 'true', 'yes', 'y', '1'].includes(raw)) return true;
    if (['inactive', 'false', 'no', 'n', '0'].includes(raw)) return false;
    return true;
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      showNotification('error', 'Excel Import Failed', 'Please select an Excel file.');
      return;
    }

    if (!excelGradeId) {
      showNotification('error', 'Excel Import Failed', 'Please select a grade.');
      return;
    }

    // Check if grade has subsections and subsection is required
    const availableSections = gradeSections
      .filter(section => section.grade_id === excelGradeId && section.is_active);

    if (availableSections.length > 0 && !excelGradeSectionId) {
      showNotification('error', 'Excel Import Failed', 'Please select a grade section.');
      return;
    }

    setIsExcelUploading(true);

    try {
      const rows = await readExcelFirstSheet(excelFile);
      if (!rows || rows.length === 0) {
        showNotification('error', 'Excel Import Failed', 'Your Excel file is empty (no rows found).');
        return;
      }

      const schoolId = localStorage.getItem('school_id');
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('school_id, name')
        .eq('id', schoolId)
        .single();

      if (schoolError || !schoolData) {
        showNotification('error', 'Excel Import Failed', 'Could not fetch school information.');
        return;
      }

      const schoolCode = schoolData.school_id.toLowerCase();
      let nextNumber = await UserIdGenerator.getNextUserNumber(schoolCode);
      if (!nextNumber || Number.isNaN(nextNumber)) nextNumber = 1;

      // Get the selected grade name
      const selectedGrade = grades.find(g => g.id === excelGradeId);
      if (!selectedGrade) {
        showNotification('error', 'Excel Import Failed', 'Selected grade not found.');
        return;
      }
      const gradeName = selectedGrade.name;

      // Get the selected subsection name if applicable
      let sectionName = null;
      if (excelGradeSectionId) {
        const selectedSection = gradeSections.find(s => s.id === excelGradeSectionId && s.grade_id === excelGradeId);
        if (selectedSection) {
          sectionName = selectedSection.name;
        }
      }

      const toInsert = [];
      const failed = []; // Track failed users with reasons
      const seenUserIds = new Set();
      const seenEmails = new Set();

      for (let i = 0; i < rows.length; i++) {
        const originalRow = rows[i];
        const normalizedRow = {};
        Object.entries(originalRow || {}).forEach(([k, v]) => {
          const nk = normalizeExcelHeader(k);
          if (nk) normalizedRow[nk] = typeof v === 'string' ? v.trim() : v;
        });

        // Only process: userid, first_name, last_name, email
        const providedUserIdRaw = String(
          getExcelValue(normalizedRow, ['userid', 'user_id'])
        ).trim();

        const firstName = String(
          getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])
        ).trim();
        const lastName = String(
          getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])
        ).trim();
        const email = String(
          getExcelValue(normalizedRow, ['email', 'email_address', 'mail'])
        ).trim();

        // Skip blank rows  
        if (!firstName && !lastName && !email) continue;

        // Validate required fields
        if (!firstName) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            reason: 'First name is required'
          });
          continue;
        }

        if (!email) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            reason: 'Email address is required'
          });
          continue;
        }

        // Validate email format
        if (!validateEmail(email)) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            reason: 'Invalid email format'
          });
          continue;
        }

        // Check for duplicate email within the Excel file
        if (seenEmails.has(email.toLowerCase())) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            reason: 'Email already exists in this Excel file'
          });
          continue;
        }
        seenEmails.add(email.toLowerCase());

        // Generate or use provided userId
        const userId = providedUserIdRaw || `${schoolCode}${nextNumber++}`;
        if (seenUserIds.has(userId)) {
          failed.push({
            row: i + 2,
            first_name: firstName,
            last_name: lastName,
            email: email,
            reason: 'Student ID already exists in this Excel file'
          });
          continue;
        }
        seenUserIds.add(userId);

        // Always generate random 6-digit password
        const finalPassword = generateRandomPassword();

        // Build grade field: if section exists, use "Grade Section", otherwise just "Grade"
        const finalGradeName = sectionName ? `${gradeName} ${sectionName}` : gradeName;

        toInsert.push({
          user_id: userId,
          first_name: firstName,
          last_name: lastName,
          school_id: schoolId,
          email,
          phone: null,
          role: 'student',
          grade: finalGradeName,
          grade_id: excelGradeId,
          grade_section_id: excelGradeSectionId || null,
          language: null,
          is_active: true, // Always active by default
          password: finalPassword,
          created_at: new Date().toISOString()
        });
      }

      // Check for existing user IDs and emails in database
      const idsToCheck = Array.from(seenUserIds);
      const emailsToCheck = toInsert.map(u => u.email);
      const existingIds = new Set();
      const existingEmails = new Set();
      const CHECK_CHUNK = 200;

      // Check for duplicate user IDs
      if (idsToCheck.length > 0) {
        for (let i = 0; i < idsToCheck.length; i += CHECK_CHUNK) {
          const chunk = idsToCheck.slice(i, i + CHECK_CHUNK);
          const { data } = await supabase.from('users').select('user_id').in('user_id', chunk);
          (data || []).forEach((u) => existingIds.add(u.user_id));
        }
      }

      // Check for duplicate emails in the same school
      if (emailsToCheck.length > 0) {
        for (let i = 0; i < emailsToCheck.length; i += CHECK_CHUNK) {
          const chunk = emailsToCheck.slice(i, i + CHECK_CHUNK);
          const { data } = await supabase
            .from('users')
            .select('email')
            .eq('school_id', schoolId)
            .in('email', chunk);
          (data || []).forEach((u) => existingEmails.add(u.email.toLowerCase()));
        }
      }

      // Check max students limit before inserting
      const maxStudents = await getSchoolMaxStudents();
      if (maxStudents !== null && maxStudents !== undefined) {
        const currentCount = await getActiveStudentsCount();
        const activeToInsert = toInsert.filter(u => u.is_active === true).length;

        if (currentCount + activeToInsert > maxStudents) {
          const canInsert = maxStudents - currentCount;
          if (canInsert <= 0) {
            showNotification('error', 'Excel Import Failed', `Maximum number of active students (${maxStudents}) has been reached. Cannot add any more students.`);
            setIsExcelUploading(false);
            return;
          }

          // Mark excess active students as failed and filter them out
          let activeCount = 0;
          toInsert.forEach((user) => {
            if (user.is_active && activeCount >= canInsert) {
              failed.push({
                row: null,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                reason: `Maximum number of active students (${maxStudents}) reached. Only ${canInsert} more can be added.`
              });
            } else {
              if (user.is_active) activeCount++;
            }
          });

          // Filter out excess active students
          let activeCount2 = 0;
          const filteredForLimit = [];
          toInsert.forEach((user) => {
            if (user.is_active && activeCount2 >= canInsert) {
              // Skip this user
            } else {
              if (user.is_active) activeCount2++;
              filteredForLimit.push(user);
            }
          });

          // Replace toInsert with filtered array
          toInsert.length = 0;
          toInsert.push(...filteredForLimit);
        }
      }

      // Remove duplicates from toInsert and add to failed list
      const filtered = [];
      toInsert.forEach((user) => {
        if (existingIds.has(user.user_id)) {
          failed.push({
            row: null, // We don't have row number here
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            reason: 'Student ID already exists in the system'
          });
        } else if (existingEmails.has(user.email.toLowerCase())) {
          failed.push({
            row: null,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            reason: 'Email address already exists in the system'
          });
        } else {
          filtered.push(user);
        }
      });

      // Insert in chunks
      const CHUNK_SIZE = 50;
      let insertedCount = 0;
      const insertErrors = [];

      for (let i = 0; i < filtered.length; i += CHUNK_SIZE) {
        const chunk = filtered.slice(i, i + CHUNK_SIZE);
        const { error } = await supabase.from('users').insert(chunk);

        if (error) {// Add all users from this chunk to failed list
          chunk.forEach((user) => {
            failed.push({
              row: null,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              reason: error.message || 'Failed to add student to database'
            });
          });
          insertErrors.push(error.message);
        } else {
          insertedCount += chunk.length;
        }
      }

      // Log activity
      if (insertedCount > 0) {
        const summaryName = `${insertedCount} students imported (Excel)`;
        await logUserActivity.created(summaryName, 'School Students', schoolId);
      }

      // Set upload results
      const results = {
        total: rows.length,
        success: insertedCount,
        failed: failed,
        hasErrors: failed.length > 0 || insertErrors.length > 0
      };

      setUploadResults(results);
      setShowUploadResults(true);

      // Refresh student list if any were added
      if (insertedCount > 0) {
        onStudentAdded && onStudentAdded();
      }
    } catch (err) {showNotification('error', 'Excel Import Failed', err.message || 'Unable to import the Excel file.');
    } finally {
      setIsExcelUploading(false);
    }
  };

  return (
    <div className="school-students-content">
      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="school-content-header">
        <h2>Student Management ({activeStudentsCount}{maxStudents !== null && maxStudents !== undefined ? ` of ${maxStudents}` : ''})</h2>
        <div className="school-header-actions">
          {!selectionMode && (
            <>
              <button className="school-students-filter-button" onClick={() => setShowFilterModal(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" stroke-linecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
                Filter
              </button>
              {!readOnly && (
                <>
                  <button className="school-students-select-btn" onClick={toggleSelectionMode}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11l3 3L22 4"></path>
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                    </svg>
                    Select
                  </button>
                  <button className="school-add-button" onClick={() => setShowAddStudentModal(true)}>+ Add Student</button>
                </>
              )}
            </>
          )}

          {!readOnly && selectionMode && (
            <>
              <button className="school-students-cancel-selection-btn" onClick={toggleSelectionMode}>
                Cancel
              </button>
              <button
                className="school-students-delete-excel-btn"
                onClick={() => setShowDeleteByExcelModal(true)}
                style={{
                  background: '#dc3545',
                  border: '1px solid #dc3545',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Delete by Excel
              </button>
              {selectedStudents.size > 0 && (
                <div className="school-students-bulk-actions">
                  <button className="school-students-promote-selected-btn" onClick={handleBulkPromote}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 19V5"></path>
                      <polyline points="5 12 12 5 19 12"></polyline>
                    </svg>
                    Promote Selected ({selectedStudents.size})
                  </button>
                  <button className="school-students-delete-selected-btn" onClick={handleBulkDelete}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    Delete Selected ({selectedStudents.size})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {dataLoading ? (
        <div className="school-loading-state">
          <div className="school-loading-spinner">⏳</div>
          <p>Loading students...</p>
        </div>
      ) : students.length === 0 ? (
        <NoDataState
          type="Students"
          icon="👥"
          message="No students have been added yet. Add your first student to get started."
          actionText="+ Add First Student"
          onAction={() => setShowAddStudentModal(true)}
        />
      ) : (
        <>
          <div className="school-students-table">
            <div className={`school-table-header ${selectionMode ? 'with-checkbox' : ''}`}>
              {selectionMode && (
                <div className="school-table-cell school-header-cell school-checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectedStudents.size === currentStudents.length && currentStudents.length > 0}
                    onChange={toggleSelectAll}
                    className="school-select-checkbox"
                  />
                </div>
              )}
              <div className="school-table-cell school-header-cell">Name</div>
              <div className="school-table-cell school-header-cell">Email</div>
              <div className="school-table-cell school-header-cell">Grade</div>
              <div className="school-table-cell school-header-cell">Status</div>
            </div>

            {currentStudents.map((student) => (
              <div
                key={student.id}
                className={`school-table-row ${selectionMode ? 'with-checkbox' : ''} ${selectedStudents.has(student.id) ? 'selected' : ''}`}
                onClick={(e) => {
                  if (selectionMode) {
                    e.stopPropagation();
                    toggleSelectStudent(student.id);
                  } else {
                    openStudentDetails(student);
                  }
                }}
              >
                {selectionMode && (
                  <div className="school-table-cell school-data-cell school-checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedStudents.has(student.id)}
                      onChange={() => toggleSelectStudent(student.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="school-select-checkbox"
                    />
                  </div>
                )}
                <div className="school-table-cell school-data-cell">
                  <div className="school-student-info">
                    <div className="school-student-avatar">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                      </svg>
                    </div>
                    <div className="school-student-name-info">
                      <div className="school-student-name-main">{student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unnamed Student'}</div>
                      <div className="school-student-email-small">{student.email || 'No email'}</div>
                    </div>
                  </div>
                </div>
                <div className="school-table-cell school-data-cell">
                  <div className="school-email-info">
                    <span className="school-email-text">{student.email || 'No email'}</span>
                  </div>
                </div>
                <div className="school-table-cell school-data-cell">
                  <div className="school-grade-info">
                    <span className="school-grade-badge">{student.grade || 'No grade'}</span>
                  </div>
                </div>
                <div className="school-table-cell school-data-cell school-status-cell">
                  <div className="school-status-info">
                    <span className={`school-status-badge ${student.is_graduated
                      ? 'school-status-graduated'
                      : student.is_active
                        ? 'school-status-active'
                        : 'school-status-inactive'
                      }`}>
                      {student.is_graduated ? 'Graduated' : student.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="school-pagination">
              <button
                className="school-pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="school-pagination-info">
                Page {currentPage} of {totalPages} ({filteredStudents.length} students)
              </span>
              <button
                className="school-pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Student Details / Edit Modal */}
      {showDetailsModal && selectedStudent && (
        <div className="school-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="school-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="school-modal-header">
              <h2>{isEditing ? 'Edit Student' : 'Student Details'}</h2>
              <button className="school-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            {!isEditing ? (
              <div className="school-student-details">
                <div className="school-details-section">
                  <div className="school-section-header">
                    <div className="school-section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                      </svg>
                    </div>
                    <h4>Personal Information</h4>
                  </div>
                  <div className="school-details-grid">
                    <div className="school-detail-item">
                      <span className="school-detail-label">First Name</span>
                      <span className="school-detail-value">{selectedStudent.first_name || '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Last Name</span>
                      <span className="school-detail-value">{selectedStudent.last_name || '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Full Name</span>
                      <span className="school-detail-value">{selectedStudent.full_name || '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Student ID</span>
                      <span className="school-detail-value">{selectedStudent.user_id || '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Email</span>
                      <span className="school-detail-value">{selectedStudent.email || '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Phone</span>
                      <span className="school-detail-value">{selectedStudent.phone || '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Grade</span>
                      <span className="school-detail-value">{selectedStudent.grade || '—'}</span>
                    </div>

                    <div className="school-detail-item">
                      <span className="school-detail-label">Password</span>
                      <span className="school-detail-value school-password-value">{selectedStudent.password || '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Status</span>
                      <span className={`school-status-badge ${selectedStudent.is_graduated
                        ? 'school-status-graduated'
                        : selectedStudent.is_active
                          ? 'school-status-active'
                          : 'school-status-inactive'
                        }`}>
                        {selectedStudent.is_graduated
                          ? 'Graduated'
                          : selectedStudent.is_active
                            ? 'Active'
                            : 'Inactive'}
                      </span>
                    </div>
                    {selectedStudent.is_graduated && (
                      <div className="school-detail-item">
                        <span className="school-detail-label">Graduation Date</span>
                        <span className="school-detail-value">
                          {new Date(selectedStudent.is_graduated).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="school-section-divider"></div>

                <div className="school-details-section">
                  <div className="school-section-header">
                    <div className="school-section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
                      </svg>
                    </div>
                    <h4>System Information</h4>
                  </div>
                  <div className="school-details-grid">
                    <div className="school-detail-item">
                      <span className="school-detail-label">Created</span>
                      <span className="school-detail-value">{selectedStudent.created_at ? new Date(selectedStudent.created_at).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className="school-detail-item">
                      <span className="school-detail-label">Last Updated</span>
                      <span className="school-detail-value">{selectedStudent.updated_at ? new Date(selectedStudent.updated_at).toLocaleDateString() : '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="school-form-actions">
                  {!readOnly && (
                    <>
                      <button type="button" className="school-student-cancel-btn" onClick={handleDeleteStudent}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                        </svg>
                        Delete Student
                      </button>
                      <button type="button" className="school-student-submit-btn" onClick={startEditSelectedStudent}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.71 7.04C21.1 6.65 21.1 6 20.71 5.63L18.37 3.29C18 2.9 17.35 2.9 16.96 3.29L15.12 5.12L18.87 8.87M3 17.25V21H6.75L17.81 9.93L14.06 6.18L3 17.25Z" />
                        </svg>
                        Edit Student
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateStudent} className="school-student-form">
                <div className="school-form-row">
                  <div className="school-form-group">
                    <label htmlFor="first_name">First Name *</label>
                    <input type="text" id="first_name" name="first_name" value={studentFormData.first_name} onChange={handleInputChange} required />
                  </div>
                  <div className="school-form-group">
                    <label htmlFor="last_name">Last Name *</label>
                    <input type="text" id="last_name" name="last_name" value={studentFormData.last_name} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="school-form-row">
                  <div className="school-form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input type="email" id="email" name="email" value={studentFormData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="school-form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={studentFormData.phone} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="school-form-row">
                  <div className="school-form-group">
                    <label>Grade *</label>
                    <select
                      name="grade_choice"
                      value={getSelectedGradeChoiceValue()}
                      onChange={handleGradeChoiceChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="">Select grade</option>
                      {grades && grades.length > 0 ? (
                        buildGradeSectionOptions().map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))
                      ) : (
                        <option value="" disabled>No grades available</option>
                      )}
                    </select>
                  </div>

                </div>

                <div className="school-form-row">
                  <div className="school-form-group">
                    <label htmlFor="new_password">Change Password</label>
                    {/* Modern 6-square password UI */}
                    <div className="dashboard-school-students-password-squares-container">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <div
                          key={index}
                          className={`dashboard-school-students-password-square ${newPassword.length > index ? 'filled' : ''} ${newPassword.length === index ? 'active' : ''}`}
                          onClick={handlePasswordSquareClick}
                        >
                          {newPassword[index] || ''}
                        </div>
                      ))}
                    </div>
                    {/* Hidden input that actually controls the password value */}
                    <input
                      type="password"
                      id="dashboard-school-students-password-input"
                      name="new_password"
                      value={newPassword}
                      onChange={handlePasswordChange}
                      className="dashboard-school-students-password-hidden-input"
                      placeholder="Enter 6-digit password (numbers only)"
                      maxLength="6"
                      autoComplete="new-password"
                    />
                    <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                      Password must be 6 digits (numbers only). Current password is shown. Edit to change it, or leave as-is to keep the same password.
                    </small>
                  </div>
                </div>

                <div className="school-form-divider"></div>

                <div className="school-form-row">
                  <div className="school-form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={studentFormData.is_active === true}
                        onChange={(e) => setStudentFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div className="school-form-actions">
                  <button type="button" className="school-student-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                  <button type="submit" className="school-student-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Add Student Modal */}
      {!readOnly && showAddStudentModal && (
        <div className="school-modal-overlay" onClick={() => handleOverlayClick(closeModal)}>
          <div className="school-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="school-modal-header">
              <h2>Add New Student</h2>
              <button className="school-modal-close" onClick={closeModal}>×</button>
            </div>

            {/* Mode Toggle */}
            <div className="school-add-mode-toggle">
              <button
                className={`school-mode-toggle-btn ${addMode === 'single' ? 'active' : ''}`}
                onClick={() => setAddMode('single')}
              >
                Single Entry
              </button>
              <button
                className={`school-mode-toggle-btn ${addMode === 'excel' ? 'active' : ''}`}
                onClick={() => setAddMode('excel')}
              >
                Upload Excel
              </button>
            </div>

            {addMode === 'single' ? (
              <form onSubmit={handleSubmitStudent} className="school-student-form">
                <div className="school-form-row">
                  <div className="school-form-group">
                    <label htmlFor="add_first_name">First Name *</label>
                    <input type="text" id="add_first_name" name="first_name" value={studentFormData.first_name} onChange={handleInputChange} required />
                  </div>
                  <div className="school-form-group">
                    <label htmlFor="add_last_name">Last Name *</label>
                    <input type="text" id="add_last_name" name="last_name" value={studentFormData.last_name} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="school-form-row">
                  <div className="school-form-group">
                    <label htmlFor="add_email">Email Address *</label>
                    <input type="email" id="add_email" name="email" value={studentFormData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="school-form-group">
                    <label htmlFor="add_phone">Phone Number</label>
                    <input type="tel" id="add_phone" name="phone" value={studentFormData.phone} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="school-form-row">
                  <div className="school-form-group">
                    <label>Grade *</label>
                    <select
                      name="grade_choice"
                      value={getSelectedGradeChoiceValue()}
                      onChange={handleGradeChoiceChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="">Select grade</option>
                      {grades && grades.length > 0 ? (
                        buildGradeSectionOptions().map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))
                      ) : (
                        <option value="" disabled>No grades available</option>
                      )}
                    </select>
                  </div>

                </div>

                <div className="school-form-divider"></div>

                <div className="school-form-row">
                  <div className="school-form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={studentFormData.is_active === true}
                        onChange={(e) => setStudentFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div className="school-form-actions">
                  <button type="button" className="school-student-cancel-btn" onClick={() => handleOverlayClick(closeModal)}>Cancel</button>
                  <button type="submit" className="school-student-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Adding Student...' : 'Add Student'}</button>
                </div>
              </form>
            ) : (
              <div className="school-excel-upload-section">
                <div className="school-excel-upload-info">
                  <h3>Upload Students via Excel</h3>
                  <p>Upload an Excel file (.xlsx or .xls) with student data. The file should contain the following columns:</p>
                  <ul className="school-excel-columns-list">
                    {/* <li><strong>userid</strong> (optional - will be auto-generated if empty)</li> */}
                    <li><strong>first_name</strong> (required)</li>
                    <li><strong>last_name</strong> (required)</li>
                    <li><strong>email</strong> (required)</li>
                  </ul>
                  <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                    <strong>Note:</strong> All students will be added to the selected grade below. Password will be auto-generated (6 digits) and status will be set to active.
                  </p>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={downloadExcelTemplate}
                    className="school-student-submit-btn"
                    style={{ marginBottom: '12px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download Excel Template
                  </button>
                </div>


                {(() => {
                  const availableSections = excelGradeId
                    ? gradeSections
                      .filter(section => section.grade_id === excelGradeId && section.is_active)
                      .sort((a, b) => a.order_index - b.order_index)
                    : [];

                  const hasSections = availableSections.length > 0;

                  return (
                    <div className="school-form-row" style={{ marginBottom: '16px' }}>
                      <div className="school-form-group" style={{ flex: hasSections ? '1' : '1' }}>
                        <label>Grade *</label>
                        <select
                          value={excelGradeId}
                          onChange={(e) => {
                            setExcelGradeId(e.target.value);
                            setExcelGradeSectionId(''); // Reset subsection when grade changes
                            setExcelFile(null); // Clear file when grade changes
                          }}
                          required
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                            fontSize: '14px',
                            backgroundColor: '#fff'
                          }}
                        >
                          <option value="">Select grade</option>
                          {grades && grades.length > 0 ? (
                            grades
                              .filter(g => g.is_active)
                              .sort((a, b) => a.order_index - b.order_index)
                              .map(grade => (
                                <option key={grade.id} value={grade.id}>{grade.name}</option>
                              ))
                          ) : (
                            <option value="" disabled>No grades available</option>
                          )}
                        </select>
                      </div>
                      {hasSections && (
                        <div className="school-form-group" style={{ flex: '1' }}>
                          <label>Grade Section *</label>
                          <select
                            value={excelGradeSectionId}
                            onChange={(e) => {
                              setExcelGradeSectionId(e.target.value);
                              setExcelFile(null); // Clear file when subsection changes
                            }}
                            required
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              borderRadius: '8px',
                              border: '1px solid #dee2e6',
                              fontSize: '14px',
                              backgroundColor: '#fff'
                            }}
                          >
                            <option value="">Select section</option>
                            {availableSections.map(section => (
                              <option key={section.id} value={section.id}>{section.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );

                })()}



                <div
                  className={`school-excel-upload-area ${isDragging ? 'dragging' : ''} ${!canUploadFile() ? 'disabled' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    opacity: canUploadFile() ? 1 : 0.6,
                    pointerEvents: canUploadFile() ? 'auto' : 'none',
                    cursor: canUploadFile() ? 'pointer' : 'not-allowed'
                  }}
                >
                  <input
                    type="file"
                    id="student-excel-file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelFileChange}
                    disabled={!canUploadFile()}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="student-excel-file"
                    className="school-excel-upload-label"
                    style={{
                      cursor: canUploadFile() ? 'pointer' : 'not-allowed',
                      pointerEvents: canUploadFile() ? 'auto' : 'none'
                    }}
                  >
                    <div className="school-excel-upload-icon">📁</div>
                    <div className="school-excel-upload-text">
                      {excelFile
                        ? excelFile.name
                        : canUploadFile()
                          ? (isDragging ? 'Drop Excel file here' : 'Click to select Excel file or drag and drop')
                          : 'Please select grade' + (excelGradeId && !excelGradeSectionId ? ' and section' : '') + ' first'
                      }
                    </div>
                    <small>Supported formats: .xlsx, .xls</small>
                  </label>
                </div>

                <div className="school-form-actions">
                  <button type="button" className="school-student-cancel-btn" onClick={() => handleOverlayClick(closeModal)}>Cancel</button>
                  <button
                    type="button"
                    className="school-student-submit-btn"
                    onClick={handleExcelUpload}
                    disabled={!excelFile || !canUploadFile() || isExcelUploading}
                  >
                    {isExcelUploading ? 'Uploading...' : 'Upload Students'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="school-modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="school-modal-content school-filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="school-modal-header">
              <h2>Filter Students</h2>
              <button className="school-modal-close" onClick={() => setShowFilterModal(false)}>×</button>
            </div>

            <div className="school-filter-content">
              <div className="school-filter-section">
                <h4>Filter By</h4>
                <div className="school-filter-options">
                  <label className="school-filter-option">
                    <input type="radio" name="filterType" value="alphabet" checked={filterType === 'alphabet'} onChange={(e) => setFilterType(e.target.value)} />
                    Alphabetical Order
                  </label>
                  <label className="school-filter-option">
                    <input type="radio" name="filterType" value="grade" checked={filterType === 'grade'} onChange={(e) => setFilterType(e.target.value)} />
                    Grade
                  </label>
                  <label className="school-filter-option">
                    <input type="radio" name="filterType" value="status" checked={filterType === 'status'} onChange={(e) => setFilterType(e.target.value)} />
                    Status
                  </label>
                </div>
              </div>

              {filterType === 'grade' && (
                <div className="school-filter-section">
                  <h4>Grade</h4>
                  <select
                    className="school-filter-select"
                    value={filterGradeId}
                    onChange={(e) => {
                      setFilterGradeId(e.target.value);
                      setFilterGradeSectionId('');
                    }}
                  >
                    <option value="">All Grades</option>
                    {grades && grades.length > 0 ? (
                      grades
                        .filter(grade => grade.is_active)
                        .sort((a, b) => a.order_index - b.order_index)
                        .map(grade => (
                          <option key={grade.id} value={grade.id}>{grade.name}</option>
                        ))
                    ) : (
                      <option value="" disabled>No grades available</option>
                    )}
                  </select>

                  {(() => {
                    const availableSections = filterGradeId
                      ? gradeSections
                        .filter(section => section.grade_id === filterGradeId && section.is_active)
                        .sort((a, b) => a.order_index - b.order_index)
                      : [];

                    return availableSections.length > 0 ? (
                      <div className="school-filter-section" style={{ marginTop: '1rem' }}>
                        <h4>Grade Section</h4>
                        <select
                          className="school-filter-select"
                          value={filterGradeSectionId}
                          onChange={(e) => setFilterGradeSectionId(e.target.value)}
                        >
                          <option value="">All Sections</option>
                          {availableSections.map(section => (
                            <option key={section.id} value={section.id}>{section.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {filterType === 'status' && (
                <div className="school-filter-section">
                  <h4>Status</h4>
                  <select className="school-filter-select" value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="graduated">Graduated</option>
                  </select>
                </div>
              )}

              <div className="school-filter-actions">
                <button className="school-student-cancel-btn" onClick={() => {
                  setFilterType('alphabet');
                  setFilterValue('');
                  setFilterGradeId('');
                  setFilterGradeSectionId('');
                  setShowFilterModal(false);
                }}>Clear Filters</button>
                <button className="school-student-submit-btn" onClick={() => setShowFilterModal(false)}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {!readOnly && showDeleteConfirm && itemToDelete && (
        <div className="school-modal-overlay" onClick={cancelDelete}>
          <div className="school-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-confirmation-header">
              <div className="school-confirmation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete Student</h2>
            </div>

            <div className="school-confirmation-content">
              <p className="school-confirmation-message">
                Are you sure you want to delete <strong>"{itemToDelete.data.first_name} {itemToDelete.data.last_name}"</strong>?
              </p>
              <p className="school-confirmation-warning">
                This action cannot be undone and will permanently remove all associated data.
              </p>
            </div>

            <div className="school-confirmation-actions">
              <button className="school-cancel-delete-btn" onClick={cancelDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="school-confirm-delete-btn" onClick={confirmDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {!readOnly && showBulkDeleteConfirm && (
        <div className="bulk-delete-confirmation-overlay">
          <div className="bulk-delete-confirmation-dialog">
            <div className="bulk-delete-confirmation-header">
              <div className="bulk-delete-icon-warning">⚠️</div>
              <h3>Delete {selectedStudents.size} Student{selectedStudents.size > 1 ? 's' : ''}?</h3>
            </div>

            <div className="bulk-delete-confirmation-body">
              <p>You are about to permanently delete <strong>{selectedStudents.size}</strong> student{selectedStudents.size > 1 ? 's' : ''}.</p>
              <p className="bulk-delete-warning-text">This action cannot be undone. All data associated with {selectedStudents.size > 1 ? 'these students' : 'this student'} will be permanently removed.</p>
            </div>

            <div className="bulk-delete-confirmation-actions">
              <button className="bulk-delete-cancel-btn" onClick={cancelBulkDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="bulk-delete-confirm-btn" onClick={confirmBulkDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete {selectedStudents.size} Student{selectedStudents.size > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Promote Confirmation Dialog */}
      {!readOnly && showBulkPromoteConfirm && (
        <div className="bulk-delete-confirmation-overlay" onClick={cancelBulkPromote}>
          <div className="bulk-delete-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="bulk-delete-confirmation-header">
              <div className="bulk-delete-icon-warning">{promotionPreview.isGraduation ? '🎓' : '⬆️'}</div>
              <h3>
                {promotionPreview.isGraduation
                  ? `Graduate ${promotionPreview.count} Student${promotionPreview.count > 1 ? 's' : ''}?`
                  : `Promote ${promotionPreview.count} Student${promotionPreview.count > 1 ? 's' : ''}?`
                }
              </h3>
            </div>

            <div className="bulk-delete-confirmation-body">
              {promotionPreview.isGraduation ? (
                <>
                  <p>
                    Graduate <strong>{promotionPreview.count}</strong> student{promotionPreview.count > 1 ? 's' : ''} from{' '}
                    <strong>{promotionPreview.fromGrade}</strong>.
                  </p>
                  <p className="bulk-delete-warning-text">
                    <strong>These students have completed the highest grade available.</strong> They will be marked as graduated with today's date.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Promote <strong>{promotionPreview.count}</strong> student{promotionPreview.count > 1 ? 's' : ''} from{' '}
                    <strong>{promotionPreview.fromGrade}</strong> to <strong>{promotionPreview.toGrade}</strong>.
                  </p>
                  <p className="bulk-delete-warning-text">
                    This updates their grade immediately.
                  </p>
                </>
              )}
            </div>

            <div className="bulk-delete-confirmation-actions">
              <button className="bulk-delete-cancel-btn" onClick={cancelBulkPromote}>
                Cancel
              </button>
              <button className="bulk-promote-confirm-btn" onClick={confirmBulkPromote}>
                {promotionPreview.isGraduation
                  ? `Graduate ${promotionPreview.count}`
                  : `Promote ${promotionPreview.count}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Results Modal */}
      {showUploadResults && uploadResults && (
        <div className="school-modal-overlay" onClick={() => setShowUploadResults(false)}>
          <div className="school-modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="school-modal-header">
              <h2>Upload Results</h2>
              <button className="school-modal-close" onClick={() => setShowUploadResults(false)}>×</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: '1' }}>
              {/* Summary Section */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: uploadResults.success > 0 ? '#f0fdf4' : '#fef2f2',
                border: `2px solid ${uploadResults.success > 0 ? '#22c55e' : '#ef4444'}`,
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: uploadResults.success > 0 ? '#22c55e' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    {uploadResults.success > 0 ? '✓' : '✗'}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
                      {uploadResults.success > 0
                        ? `${uploadResults.success} Student${uploadResults.success !== 1 ? 's' : ''} Added Successfully`
                        : 'No Students Were Added'
                      }
                    </h3>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                      {uploadResults.total} total row{uploadResults.total !== 1 ? 's' : ''} processed
                    </p>
                  </div>
                </div>

                {uploadResults.failed.length > 0 && (
                  <div style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <p style={{ margin: 0, color: '#dc2626', fontWeight: '600', fontSize: '14px' }}>
                      {uploadResults.failed.length} Student{uploadResults.failed.length !== 1 ? 's' : ''} Could Not Be Added
                    </p>
                  </div>
                )}
              </div>

              {/* Failed Students List - Grouped by Reason */}
              {uploadResults.failed.length > 0 && (() => {
                // Group failed students by reason
                const groupedByReason = uploadResults.failed.reduce((acc, item) => {
                  const reason = item.reason || 'Unknown error';
                  if (!acc[reason]) {
                    acc[reason] = [];
                  }
                  acc[reason].push(item);
                  return acc;
                }, {});

                // Convert to array of groups
                const reasonGroups = Object.entries(groupedByReason).map(([reason, students]) => ({
                  reason,
                  students,
                  count: students.length
                }));

                return (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Failed Students by Reason</h3>
                      <button
                        onClick={downloadFailedUsersReport}
                        style={{
                          padding: '8px 16px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download Report
                      </button>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      paddingRight: '8px'
                    }}>
                      {reasonGroups.map((group, groupIndex) => (
                        <div
                          key={groupIndex}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: 'white'
                          }}
                        >
                          {/* Group Header */}
                          <div style={{
                            padding: '14px 16px',
                            background: '#fef2f2',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: '#ef4444'
                              }}></div>
                              <span style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#1f2937'
                              }}>
                                {group.reason}
                              </span>
                            </div>
                            <span style={{
                              padding: '4px 12px',
                              background: '#fee2e2',
                              color: '#991b1b',
                              borderRadius: '12px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              {group.count} student{group.count !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {/* Students in this group */}
                          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Row</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>First Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Last Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Email</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.students.map((item, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      borderBottom: index < group.students.length - 1 ? '1px solid #f3f4f6' : 'none',
                                      background: index % 2 === 0 ? 'white' : '#f9fafb'
                                    }}
                                  >
                                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{item.row || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.first_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.last_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.email || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Success Message */}
              {uploadResults.success > 0 && uploadResults.failed.length === 0 && (
                <div style={{
                  padding: '16px',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #22c55e',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, color: '#166534', fontSize: '15px', fontWeight: '500' }}>
                    🎉 All students were added successfully!
                  </p>
                </div>
              )}
            </div>

            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowUploadResults(false);
                  setUploadResults(null);
                  if (uploadResults.success > 0) {
                    closeModal();
                  }
                }}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {uploadResults.success > 0 ? 'Close' : 'Try Again'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete by Excel Modal */}
      {!readOnly && showDeleteByExcelModal && (
        <div className="school-modal-overlay" onClick={() => setShowDeleteByExcelModal(false)}>
          <div className="school-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="school-modal-header">
              <h2>Delete Students by Excel</h2>
              <button className="school-modal-close" onClick={() => {
                setShowDeleteByExcelModal(false);
                setDeleteExcelFile(null);
                setDeleteExcelResults(null);
              }}>×</button>
            </div>

            <div className="school-excel-upload-section">
              <div className="school-excel-upload-info">
                <h3>Deactivate Students via Excel</h3>
                <p>Upload an Excel file (.xlsx or .xls) with student emails. Students matching these emails will be marked as inactive.</p>
                <ul className="school-excel-columns-list">
                  <li><strong>email</strong> (required)</li>
                </ul>
                <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                  <strong>Note:</strong> Students will be deactivated (set to inactive status) based on their email addresses. The file can also include first_name and last_name columns for reference.
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={downloadDeleteExcelTemplate}
                  className="school-student-submit-btn"
                  style={{ marginBottom: '12px' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download Excel Template
                </button>
              </div>

              <div
                className={`school-excel-upload-area ${deleteExcelDragging ? 'dragging' : ''}`}
                onDragOver={handleDeleteExcelDragOver}
                onDragLeave={handleDeleteExcelDragLeave}
                onDrop={handleDeleteExcelDrop}
              >
                <input
                  type="file"
                  id="delete-student-excel-file"
                  accept=".xlsx,.xls"
                  onChange={handleDeleteExcelFileChange}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="delete-student-excel-file"
                  className="school-excel-upload-label"
                >
                  <div className="school-excel-upload-icon">📁</div>
                  <div className="school-excel-upload-text">
                    {deleteExcelFile
                      ? deleteExcelFile.name
                      : (deleteExcelDragging ? 'Drop Excel file here' : 'Click to select Excel file or drag and drop')
                    }
                  </div>
                  <small>Supported formats: .xlsx, .xls</small>
                </label>
              </div>

              <div className="school-form-actions">
                <button
                  type="button"
                  className="school-student-cancel-btn"
                  onClick={() => {
                    setShowDeleteByExcelModal(false);
                    setDeleteExcelFile(null);
                    setDeleteExcelResults(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="school-student-submit-btn"
                  onClick={handleDeleteByExcel}
                  disabled={!deleteExcelFile || isDeleteExcelUploading}
                  style={{
                    background: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {isDeleteExcelUploading ? 'Processing...' : 'Deactivate Students'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete by Excel Results Modal */}
      {showDeleteExcelResults && deleteExcelResults && (
        <div className="school-modal-overlay" onClick={() => setShowDeleteExcelResults(false)}>
          <div className="school-modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="school-modal-header">
              <h2>Deactivation Results</h2>
              <button className="school-modal-close" onClick={() => setShowDeleteExcelResults(false)}>×</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: '1' }}>
              {/* Summary Section */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: deleteExcelResults.success > 0 ? '#f0fdf4' : '#fef2f2',
                border: `2px solid ${deleteExcelResults.success > 0 ? '#22c55e' : '#ef4444'}`,
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: deleteExcelResults.success > 0 ? '#22c55e' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    {deleteExcelResults.success > 0 ? '✓' : '✗'}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
                      {deleteExcelResults.success > 0
                        ? `${deleteExcelResults.success} Student${deleteExcelResults.success !== 1 ? 's' : ''} Deactivated Successfully`
                        : 'No Students Were Deactivated'
                      }
                    </h3>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                      {deleteExcelResults.total} total row{deleteExcelResults.total !== 1 ? 's' : ''} processed
                    </p>
                  </div>
                </div>

                {deleteExcelResults.failed.length > 0 && (
                  <div style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <p style={{ margin: 0, color: '#dc2626', fontWeight: '600', fontSize: '14px' }}>
                      {deleteExcelResults.failed.length} Student{deleteExcelResults.failed.length !== 1 ? 's' : ''} Could Not Be Deactivated
                    </p>
                  </div>
                )}
              </div>

              {/* Failed Students List - Grouped by Reason */}
              {deleteExcelResults.failed.length > 0 && (() => {
                const groupedByReason = deleteExcelResults.failed.reduce((acc, item) => {
                  const reason = item.reason || 'Unknown error';
                  if (!acc[reason]) {
                    acc[reason] = [];
                  }
                  acc[reason].push(item);
                  return acc;
                }, {});

                const reasonGroups = Object.entries(groupedByReason).map(([reason, students]) => ({
                  reason,
                  students,
                  count: students.length
                }));

                return (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Failed Deactivations by Reason</h3>
                      <button
                        onClick={downloadFailedDeleteReport}
                        style={{
                          padding: '8px 16px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download Report
                      </button>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      paddingRight: '8px'
                    }}>
                      {reasonGroups.map((group, groupIndex) => (
                        <div
                          key={groupIndex}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: 'white'
                          }}
                        >
                          <div style={{
                            padding: '14px 16px',
                            background: '#fef2f2',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: '#ef4444'
                              }}></div>
                              <span style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#1f2937'
                              }}>
                                {group.reason}
                              </span>
                            </div>
                            <span style={{
                              padding: '4px 12px',
                              background: '#fee2e2',
                              color: '#991b1b',
                              borderRadius: '12px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              {group.count} student{group.count !== 1 ? 's' : ''}
                            </span>
                          </div>

                          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Row</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>First Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Last Name</th>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>Email</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.students.map((item, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      borderBottom: index < group.students.length - 1 ? '1px solid #f3f4f6' : 'none',
                                      background: index % 2 === 0 ? 'white' : '#f9fafb'
                                    }}
                                  >
                                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{item.row || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.first_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.last_name || '—'}</td>
                                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{item.email || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Success Message */}
              {deleteExcelResults.success > 0 && deleteExcelResults.failed.length === 0 && (
                <div style={{
                  padding: '16px',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #22c55e',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, color: '#166534', fontSize: '15px', fontWeight: '500' }}>
                    🎉 All students were deactivated successfully!
                  </p>
                </div>
              )}
            </div>

            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowDeleteExcelResults(false);
                  setDeleteExcelResults(null);
                  if (deleteExcelResults.success > 0) {
                    setShowDeleteByExcelModal(false);
                    setDeleteExcelFile(null);
                  }
                }}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {deleteExcelResults.success > 0 ? 'Close' : 'Try Again'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {/* Max Limit Error Overlay */}
      {showMaxLimitError && (
        <div className="max-limit-error-overlay" onClick={() => setShowMaxLimitError(false)}>
          <div className="max-limit-error-content" onClick={(e) => e.stopPropagation()}>
            <div className="max-limit-error-header">
              <div className="max-limit-error-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Maximum Limit Reached</h2>
            </div>
            <div className="max-limit-error-body">
              <p className="max-limit-error-message">
                The maximum number of active {maxLimitError.type} ({maxLimitError.max}) has been reached.
              </p>
              <p className="max-limit-error-details">
                Current active {maxLimitError.type}: {maxLimitError.current} of {maxLimitError.max}
              </p>
              <p className="max-limit-error-note">
                You cannot add more {maxLimitError.type} or change inactive {maxLimitError.type} to active status until the limit is increased or some {maxLimitError.type} are deactivated.
              </p>
            </div>
            <div className="max-limit-error-actions">
              <button className="max-limit-error-close-btn" onClick={() => setShowMaxLimitError(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        autoClose={true}
        autoCloseDelay={4000}
      />
    </div>
  );
};

export default DashboardSchoolStudents;
